'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useConnectorStore } from '@/lib/connectors/store';
import { X, Plus } from 'lucide-react';

export function ConnectorForm() {
  const { currentConnector, setCurrentConnector } = useConnectorStore();

  const [name, setName] = useState(currentConnector?.name || '');
  const [displayName, setDisplayName] = useState(currentConnector?.display_name || '');
  const [description, setDescription] = useState(currentConnector?.description || '');
  const [baseUrl, setBaseUrl] = useState(currentConnector?.base_url || '');
  const [version, setVersion] = useState(currentConnector?.version || '1.0.0');
  const [headers, setHeaders] = useState<Array<{ key: string; value: string }>>(
    Object.entries(currentConnector?.default_headers || {}).map(([key, value]) => ({ key, value }))
  );

  // Update local state when currentConnector changes
  useEffect(() => {
    if (currentConnector) {
      setName(currentConnector.name || '');
      setDisplayName(currentConnector.display_name || '');
      setDescription(currentConnector.description || '');
      setBaseUrl(currentConnector.base_url || '');
      setVersion(currentConnector.version || '1.0.0');
      setHeaders(
        Object.entries(currentConnector.default_headers || {}).map(([key, value]) => ({ key, value }))
      );
    }
  }, [currentConnector?.id]);

  // Sync changes back to store
  const updateStore = () => {
    if (!currentConnector) return;

    const headersRecord: Record<string, string> = {};
    headers.forEach(h => {
      if (h.key) headersRecord[h.key] = h.value;
    });

    setCurrentConnector({
      ...currentConnector,
      name,
      display_name: displayName || null,
      description: description || null,
      base_url: baseUrl || null,
      version,
      default_headers: headersRecord,
    });
  };

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setHeaders(newHeaders);
  };

  const removeHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>
            Configure the basic settings for your connector
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Connector ID</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={updateStore}
                placeholder="my-connector"
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier (lowercase, no spaces)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                onBlur={updateStore}
                placeholder="1.0.0"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onBlur={updateStore}
              placeholder="My Custom Connector"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={updateStore}
              placeholder="Describe what this connector does..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="base_url">Base URL</Label>
            <Input
              id="base_url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              onBlur={updateStore}
              placeholder="https://api.example.com/v1"
            />
            <p className="text-sm text-muted-foreground">
              The base URL for all API requests. Operation paths will be appended to this.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Default Headers</CardTitle>
          <CardDescription>
            Headers to include with every request
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {headers.map((header, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  placeholder="Header name"
                  value={header.key}
                  onChange={(e) => updateHeader(index, 'key', e.target.value)}
                  onBlur={updateStore}
                  className="flex-1"
                />
                <Input
                  placeholder="Value"
                  value={header.value}
                  onChange={(e) => updateHeader(index, 'value', e.target.value)}
                  onBlur={updateStore}
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    removeHeader(index);
                    setTimeout(updateStore, 0);
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addHeader}>
              <Plus className="size-4 mr-2" />
              Add Header
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
